// Given one line (string) OR an OversizedLineMarker from the line splitter,
// returns a discriminated parse result. Never throws.
//
// Known event shapes use zod `.passthrough()` per known type/subtype (from
// research/claude-headless-contract.md's event table + the real probe
// fixtures) — unknown extra fields pass through untouched. An unknown
// type/subtype still parses (as JSON) but is wrapped as a passthrough
// "unknown" event rather than a specific known shape. Malformed JSON never
// throws — returns a parse_error result carrying the raw text.
import { z } from "zod";
import type { OversizedLineMarker, SplitterYield } from "./stream-json-line-splitter-bounded.js";

const SystemInitEventSchema = z
  .object({
    type: z.literal("system"),
    subtype: z.literal("init"),
    session_id: z.string().optional(),
    cwd: z.string().optional(),
    tools: z.array(z.string()).optional(),
    mcp_servers: z
      .array(z.object({ name: z.string(), status: z.string() }).passthrough())
      .optional(),
    model: z.string().optional(),
    permissionMode: z.string().optional(),
    claude_code_version: z.string().optional(),
    plugins: z.array(z.unknown()).optional(),
    agents: z.array(z.string()).optional(),
    skills: z.array(z.string()).optional(),
    memory_paths: z.record(z.string(), z.unknown()).optional(),
    uuid: z.string().optional(),
  })
  .passthrough();

const HookStartedEventSchema = z
  .object({
    type: z.literal("hook_started"),
    session_id: z.string().optional(),
    hook_event_name: z.string(),
    hook_index: z.number().optional(),
    matcher: z.string().optional(),
    uuid: z.string().optional(),
  })
  .passthrough();

const HookResponseEventSchema = z
  .object({
    type: z.literal("hook_response"),
    session_id: z.string().optional(),
    hook_event_name: z.string(),
    status: z.string().optional(),
    output: z.unknown().optional(),
    permissionDecision: z.string().optional(),
    uuid: z.string().optional(),
  })
  .passthrough();

const ResultEventSchema = z
  .object({
    type: z.literal("result"),
    subtype: z.string().optional(),
    is_error: z.boolean().optional(),
    result: z.string().optional(),
    structured_output: z.unknown().optional(),
    session_id: z.string().optional(),
    api_error_status: z.number().nullable().optional(),
    permission_denials: z.array(z.unknown()).optional(),
    terminal_reason: z.string().optional(),
    uuid: z.string().optional(),
  })
  .passthrough();

const AssistantEventSchema = z.object({ type: z.literal("assistant") }).passthrough();
const UserEventSchema = z.object({ type: z.literal("user") }).passthrough();
const ApiRetryEventSchema = z
  .object({ type: z.literal("system"), subtype: z.literal("api_retry") })
  .passthrough();
const PermissionDeniedEventSchema = z
  .object({ type: z.literal("permission_denied") })
  .passthrough();

export interface SystemInitInventory {
  commands: string[];
  agents: string[];
  // Best-effort: system/init does NOT directly list hooks per the researched
  // contract (research/claude-headless-contract.md §5). Real hook evidence
  // comes from hook-lifecycle-evidence-collector.ts watching
  // hook_started/hook_response events instead — this field stays empty
  // unless a future binary version starts exposing it.
  hooks: string[];
  mcpServers: Array<{ name: string; status: string }>;
  settingsSource: string | undefined;
}

export type ParsedEvent =
  | {
      kind: "system_init";
      raw: z.infer<typeof SystemInitEventSchema>;
      inventory: SystemInitInventory;
    }
  | { kind: "hook_started"; raw: z.infer<typeof HookStartedEventSchema> }
  | { kind: "hook_response"; raw: z.infer<typeof HookResponseEventSchema> }
  | { kind: "result"; raw: z.infer<typeof ResultEventSchema> }
  | { kind: "assistant"; raw: z.infer<typeof AssistantEventSchema> }
  | { kind: "user"; raw: z.infer<typeof UserEventSchema> }
  | { kind: "system_api_retry"; raw: z.infer<typeof ApiRetryEventSchema> }
  | { kind: "permission_denied"; raw: z.infer<typeof PermissionDeniedEventSchema> }
  | { kind: "unknown"; raw: Record<string, unknown> }
  | { kind: "oversized_line"; marker: OversizedLineMarker }
  | { kind: "parse_error"; raw: string; error: string };

function buildSystemInitInventory(
  event: z.infer<typeof SystemInitEventSchema>,
): SystemInitInventory {
  return {
    commands: (event as { slash_commands?: string[] }).slash_commands ?? [],
    agents: event.agents ?? [],
    hooks: [],
    mcpServers: event.mcp_servers ?? [],
    settingsSource: (event as { settingsSource?: string }).settingsSource,
  };
}

function isOversizedMarker(line: SplitterYield): line is OversizedLineMarker {
  return (
    typeof line === "object" && line !== null && "kind" in line && line.kind === "oversized_line"
  );
}

export function parseStreamJsonEvent(line: SplitterYield): ParsedEvent {
  if (isOversizedMarker(line)) {
    return { kind: "oversized_line", marker: line };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(line);
  } catch (err) {
    return {
      kind: "parse_error",
      raw: line,
      error: err instanceof Error ? err.message : String(err),
    };
  }

  if (typeof parsed !== "object" || parsed === null) {
    return { kind: "parse_error", raw: line, error: "top-level JSON value is not an object" };
  }

  const obj = parsed as Record<string, unknown>;

  if (obj.type === "system" && obj.subtype === "init") {
    const result = SystemInitEventSchema.safeParse(obj);
    if (result.success) {
      return {
        kind: "system_init",
        raw: result.data,
        inventory: buildSystemInitInventory(result.data),
      };
    }
  }
  if (obj.type === "system" && obj.subtype === "api_retry") {
    const result = ApiRetryEventSchema.safeParse(obj);
    if (result.success) return { kind: "system_api_retry", raw: result.data };
  }
  if (obj.type === "hook_started") {
    const result = HookStartedEventSchema.safeParse(obj);
    if (result.success) return { kind: "hook_started", raw: result.data };
  }
  if (obj.type === "hook_response") {
    const result = HookResponseEventSchema.safeParse(obj);
    if (result.success) return { kind: "hook_response", raw: result.data };
  }
  if (obj.type === "result") {
    const result = ResultEventSchema.safeParse(obj);
    if (result.success) return { kind: "result", raw: result.data };
  }
  if (obj.type === "assistant") {
    const result = AssistantEventSchema.safeParse(obj);
    if (result.success) return { kind: "assistant", raw: result.data };
  }
  if (obj.type === "user") {
    const result = UserEventSchema.safeParse(obj);
    if (result.success) return { kind: "user", raw: result.data };
  }
  if (obj.type === "permission_denied") {
    const result = PermissionDeniedEventSchema.safeParse(obj);
    if (result.success) return { kind: "permission_denied", raw: result.data };
  }

  // Unknown type/subtype (or a known type whose shape failed schema) — pass
  // through, never throw.
  return { kind: "unknown", raw: obj };
}
